import { useEffect, useRef } from "react";

import { useFormState } from "common/hooks";
import {
  IPatientWriteBody,
  PatientKind,
  WeightUnitOfMeasure,
  SexKind,
  UploadedImage,
} from "common/types";
import { Optional } from "common/utils/types";
import { FetchError } from "common/fetch/types";
import { MONTH_SHORT_NAMES } from "common/constants/dates";
import { getWeightUnitOfMeasureLabel } from "common/utils/patients";

import TextInput, { TextInputProps } from "client/common/components/inputs/TextInput";
import Select from "client/common/components/inputs/Select";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import ImageInput from "client/common/components/inputs/ImageInput";

import styles from "../../styles/PetForm.module.less";

const noop = () => null;

type FormValues = {
  name: string;
  sex: SexKind;
  weight_in_units: string;
  weight_unit_of_measure: WeightUnitOfMeasure;
  birth_day: string;
  birth_month: string;
  birth_year: string;
  species: PatientKind;
  custom_species: string;
  allergies: string;
  image: UploadedImage | null;
};

type PetFormProps = {
  initialValues?: FormValues;
  onSubmit: (values: FormValues) => void;
  submitText: string;
  isLoading: boolean;
  error: Optional<FetchError<IPatientWriteBody>>;
  renderExtraContent?: () => React.ReactNode;
};

type CustomSpeciesInputProps = Omit<TextInputProps, "ref">;

const CustomSpeciesInput = (props: CustomSpeciesInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return <TextInput {...props} ref={inputRef} />;
};

const PetForm = ({
  submitText,
  initialValues,
  onSubmit,
  error,
  isLoading,
  renderExtraContent = noop,
}: PetFormProps) => {
  const [values, updateField] = useFormState<FormValues>({
    name: initialValues?.name ?? "",
    sex: initialValues?.sex ?? SexKind.Female,
    weight_in_units: initialValues?.weight_in_units ?? "",
    weight_unit_of_measure: initialValues?.weight_unit_of_measure || WeightUnitOfMeasure.Pound,
    birth_day: initialValues?.birth_day ?? "",
    birth_month: initialValues?.birth_month ?? "",
    birth_year: initialValues?.birth_year ?? "",
    species: initialValues?.species ?? PatientKind.Canine,
    custom_species: initialValues?.custom_species ?? "",
    allergies: initialValues?.allergies ?? "",
    image: initialValues?.image ?? null,
  });

  const canSubmit =
    !!values.name &&
    values.sex !== SexKind.Unknown &&
    (values.species !== PatientKind.Other || !!values.custom_species);

  return (
    <DrawerLayout.Form
      submitText={submitText}
      onSubmit={() => onSubmit(values)}
      isLoading={isLoading}
      canSubmit={canSubmit}
      error={error}
    >
      <DrawerLayout.Form.Group>
        <div className={styles.nameContainer}>
          <TextInput
            label="Name"
            value={values.name}
            onChange={updateField("name")}
            errors={error?.errors.name}
          />
          <div className={styles.nameHelp}>Enter one pet name only.</div>
        </div>
      </DrawerLayout.Form.Group>
      <DrawerLayout.Form.Group>
        <Select
          label="Sex"
          value={values.sex}
          onChange={(val) => updateField("sex")(val as SexKind)}
          errors={error?.errors.sex}
        >
          {initialValues?.sex === SexKind.Unknown && (
            <Select.Option disabled={true} value={SexKind.Unknown}>
              Unknown
            </Select.Option>
          )}
          <Select.Option value={SexKind.Female}>Female</Select.Option>
          <Select.Option value={SexKind.Male}>Male</Select.Option>
        </Select>
      </DrawerLayout.Form.Group>
      <DrawerLayout.Form.Group>
        {/** NOTE: if expanding this to PIMS client-side patient UI, consider adding input for UOM */}
        <TextInput
          type="number"
          label={`Weight (${getWeightUnitOfMeasureLabel(values.weight_unit_of_measure)})`}
          value={values.weight_in_units}
          onChange={updateField("weight_in_units")}
          errors={error?.errors.weight_in_units}
        />
      </DrawerLayout.Form.Group>
      <DrawerLayout.Form.Group align="horizontal">
        <Select
          label="Birth Month"
          value={values.birth_month}
          onChange={updateField("birth_month")}
          errors={error?.errors.birth_month}
          className={styles.monthInput}
        >
          {MONTH_SHORT_NAMES.map((month, index) => (
            <Select.Option value={`${index + 1}`} key={month}>
              {month}
            </Select.Option>
          ))}
        </Select>
        <TextInput
          type="number"
          label="Birth Day"
          value={values.birth_day}
          maxLength={2}
          onChange={updateField("birth_day")}
          errors={error?.errors.birth_day}
        />
        <TextInput
          type="number"
          label="Birth Year"
          value={values.birth_year}
          maxLength={4}
          onChange={updateField("birth_year")}
          errors={error?.errors.birth_year}
        />
      </DrawerLayout.Form.Group>
      <DrawerLayout.Form.Group>
        <TextInput
          type="text"
          label="Allergies"
          value={values.allergies}
          onChange={updateField("allergies")}
          errors={error?.errors.allergies}
        />
      </DrawerLayout.Form.Group>
      <DrawerLayout.Form.Group>
        <Select
          label="Species"
          value={values.species}
          onChange={(val) => updateField("species")(val as PatientKind)}
          errors={error?.errors.species}
        >
          {Object.entries(PatientKind).map(
            ([name, value]) =>
              // avoid showing unspecified as an option
              (value !== PatientKind.Unspecified ||
                initialValues?.species === PatientKind.Unspecified) && (
                <Select.Option value={value} key={value}>
                  {name}
                </Select.Option>
              ),
          )}
        </Select>
      </DrawerLayout.Form.Group>
      {values.species === PatientKind.Other && (
        <DrawerLayout.Form.Group>
          <CustomSpeciesInput
            type="text"
            label="Custom Species"
            value={values.custom_species}
            onChange={updateField("custom_species")}
            errors={error?.errors.custom_species}
          />
        </DrawerLayout.Form.Group>
      )}
      {/* TODO add back when we come back to client facing PIMS features */}
      {false && (
        <ImageInput
          label="Image"
          variant="circle"
          value={values.image}
          onChange={updateField("image")}
          errors={error?.errors.image_id}
        />
      )}
      {renderExtraContent()}
    </DrawerLayout.Form>
  );
};

export default PetForm;
